import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, RefreshCw, Mail, Home, Wifi, WifiOff } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface ErrorPageProps {
  errorType?: 'network' | 'server' | 'ai' | 'general';
  onRetry?: () => void;
  onContactSupport?: () => void;
  onGoHome?: () => void;
}

const errorMessages = {
  network: {
    title: "Connection Lost",
    subtitle: "Check your internet connection and try again.",
    icon: WifiOff
  },
  server: {
    title: "Server Unavailable",
    subtitle: "Our servers are taking a quick break. We'll be back soon!",
    icon: AlertCircle
  },
  ai: {
    title: "AI Service Temporarily Down",
    subtitle: "Gemini AI is currently unavailable. Please try again in a moment.",
    icon: AlertCircle
  },
  general: {
    title: "Oops! Something went wrong.",
    subtitle: "Don't worry — let's fix this together.",
    icon: AlertCircle
  }
};

export const ErrorPage: React.FC<ErrorPageProps> = ({ 
  errorType = 'general',
  onRetry,
  onContactSupport,
  onGoHome
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  
  const error = errorMessages[errorType];
  const ErrorIcon = error.icon;

  const handleRetry = async () => {
    setIsRetrying(true);
    
    // Simulate retry attempt
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Simulate success (in real app, this would be actual retry logic)
    const success = Math.random() > 0.3; // 70% success rate
    
    if (success) {
      setShowSuccess(true);
      // Trigger confetti
      if (!prefersReducedMotion) {
        console.log('Confetti burst on successful retry!');
      }
      setTimeout(() => {
        onRetry?.();
      }, 1500);
    } else {
      setIsRetrying(false);
    }
  };

  const handleContactSupport = () => {
    onContactSupport?.();
  };

  const handleGoHome = () => {
    onGoHome?.();
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-100 via-blue-50 to-teal-100 dark:from-green-900/20 dark:via-blue-900/20 dark:to-teal-900/20 flex items-center justify-center pt-20">
        <motion.div
          className="text-center"
          initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mb-6"
            animate={prefersReducedMotion ? {} : { rotate: 360 }}
            transition={{ duration: 1 }}
          >
            <Wifi className="w-12 h-12 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            All Fixed!
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Connection restored. Redirecting you back...
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-blue-50 to-purple-100 dark:from-orange-900/20 dark:via-blue-900/20 dark:to-purple-900/20 flex items-center justify-center pt-20">
      {/* Animated Background Waves */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={prefersReducedMotion ? {} : {
            background: [
              "radial-gradient(circle at 20% 80%, #FF6B35 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)",
              "radial-gradient(circle at 40% 40%, #8B5CF6 0%, transparent 50%)",
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        
        {/* Floating Elements */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-32 h-32 bg-orange-400/20 rounded-full blur-xl"
          animate={prefersReducedMotion ? {} : {
            y: [0, -30, 0],
            x: [0, 20, 0],
          }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-blue-400/20 rounded-full blur-xl"
          animate={prefersReducedMotion ? {} : {
            y: [0, 25, 0],
            x: [0, -15, 0],
          }}
          transition={{ duration: 5, repeat: Infinity, delay: 1 }}
        />
      </div>

      <div className="relative z-10 max-w-md mx-auto px-4 text-center">
        {/* Error Icon with Animation */}
        <motion.div
          className="mb-8"
          initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, type: 'spring', bounce: 0.4 }}
        >
          <div className="relative inline-block">
            {/* Broken Chain Animation */}
            <motion.div
              className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mb-4"
              animate={prefersReducedMotion ? {} : {
                rotate: [0, -5, 5, 0],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <ErrorIcon className="w-12 h-12 text-white" />
            </motion.div>
            
            {/* Glow Effect */}
            <motion.div
              className="absolute inset-0 w-24 h-24 bg-red-500/30 rounded-full blur-xl"
              animate={prefersReducedMotion ? {} : {
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>

        {/* Error Message */}
        <motion.div
          className="mb-8"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {error.title}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
            {error.subtitle}
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className="space-y-4"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {/* Retry Button */}
          <motion.button
            onClick={handleRetry}
            disabled={isRetrying}
            className="w-full px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-semibold text-lg flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={prefersReducedMotion ? {} : { 
              scale: isRetrying ? 1 : 1.05, 
              boxShadow: "0 20px 40px rgba(255, 107, 53, 0.3)" 
            }}
            whileTap={prefersReducedMotion ? {} : { scale: isRetrying ? 1 : 0.95 }}
          >
            {isRetrying ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <RefreshCw className="w-5 h-5" />
                </motion.div>
                Retrying...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Retry
              </>
            )}
          </motion.button>

          {/* Secondary Actions */}
          <div className="flex gap-4">
            <motion.button
              onClick={handleContactSupport}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-full font-medium flex items-center justify-center gap-2"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            >
              <Mail className="w-4 h-4" />
              Contact Support
            </motion.button>
            
            <motion.button
              onClick={handleGoHome}
              className="flex-1 px-6 py-3 bg-white/10 dark:bg-gray-800/10 text-gray-900 dark:text-white rounded-full font-medium border border-white/20 dark:border-gray-700/20 hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200 flex items-center justify-center gap-2"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            >
              <Home className="w-4 h-4" />
              Go Home
            </motion.button>
          </div>
        </motion.div>

        {/* Help Text */}
        <motion.div
          className="mt-8 p-4 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <p className="text-sm text-gray-600 dark:text-gray-300">
            If the problem persists, try checking your internet connection or contact our support team for assistance.
          </p>
        </motion.div>
      </div>
    </div>
  );
};